/** @format */

import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';

import { Chain } from '@shared/data-access/interfaces';
import { PinnedContentRequest, AemPinnedContentResponse } from '@plp/data-access/interfaces';
import {
  PINNED_CONTENT_RETRY_COUNT,
  PINNED_CONTENT_SPOT_ENDPOINT,
} from '@plp/data-access/constants';

/**
 * @description
 * Retrieves AEM Pinned Content Spot (PCS) data. The request currently resolves against a
 * mocked/fake API response (served via MSW locally). Swapping to the real AEM API only
 * requires changing the endpoint constant / environment base — no consumer refactor needed.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductListAemDao {
  private readonly http = inject(HttpClient);
  private readonly chain = inject(Chain);

  /**
   * @description Fetches the AEM PCS data for the supplied page context.
   * @param request - PCS lookup parameters.
   * @returns Observable of the PCS response, or throws error when the fetch fails.
   */
  getPinnedContentData$(
    request: PinnedContentRequest,
    retries = PINNED_CONTENT_RETRY_COUNT,
  ): Observable<AemPinnedContentResponse> {
    const params = this.buildParams(request);

    return this.http.get<AemPinnedContentResponse>(PINNED_CONTENT_SPOT_ENDPOINT, { params }).pipe(
      retry(retries),
      timeout(10000),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  private buildParams(request: PinnedContentRequest): HttpParams {
    let params = new HttpParams()
      .set('pageType', request.pageType)
      .set('chain', request.chain ?? this.chain.chainMarketingIdentifierAbbr);
    if (request.name) {
      params = params.set('name', request.name);
    }

    return params;
  }
}
